import type {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
} from "react";
import { cn } from "@/lib/utils";

interface ResizeHandleSeparatorMetrics {
  valueMax: number;
  valueMin: number;
  valueNow: number;
}

interface ResizeHandleProps {
  ariaLabel?: string;
  onMouseDown: (event: ReactMouseEvent) => void;
  className?: string;
  /** Container edge the handle sits on (`start` = left in LTR). Default `end` matches left-side sidebars. */
  edge?: "start" | "end";
  onKeyDown?: (event: ReactKeyboardEvent<HTMLElement>) => void;
  /**
   * When set, renders an adjustable vertical splitter (`role="separator"`) with width exposed to ATs.
   */
  separatorMetrics?: ResizeHandleSeparatorMetrics;
}

export const ResizeHandle = ({
  ariaLabel,
  onMouseDown,
  className,
  edge = "end",
  onKeyDown,
  separatorMetrics,
}: ResizeHandleProps) => {
  const edgePositionClass = edge === "start" ? "-left-2" : "-right-2";

  const mergedClassName = cn(
    "absolute top-0 h-full w-4 cursor-ew-resize select-none outline-none",
    "focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-slate-950 focus-visible:ring-offset-1",
    "dark:focus-visible:ring-slate-300",
    edgePositionClass,
    "after:absolute after:left-1/2 after:top-0 after:h-full after:w-0.5 after:bg-transparent",
    "hover:after:bg-gray-300",
    "active:after:bg-violet-700",
    "border-0 bg-transparent p-0",
    className,
  );

  if (separatorMetrics != null) {
    return (
      // biome-ignore lint/a11y/useSemanticElements: <hr> is a replaced element; ::after for the drag line is unreliable. Interactive splitter uses div+role per WAI-ARIA.
      <div
        role="separator"
        aria-orientation="vertical"
        aria-valuenow={Math.round(separatorMetrics.valueNow)}
        aria-valuemin={Math.round(separatorMetrics.valueMin)}
        aria-valuemax={Math.round(separatorMetrics.valueMax)}
        aria-label={ariaLabel ?? "Resize panel"}
        tabIndex={0}
        className={mergedClassName}
        onMouseDown={onMouseDown}
        onKeyDown={onKeyDown}
      />
    );
  }

  return (
    <button
      type="button"
      className={mergedClassName}
      onMouseDown={onMouseDown}
      onKeyDown={onKeyDown}
      onClick={(event) => event.stopPropagation()}
      aria-label={ariaLabel ?? "Resize"}
    />
  );
};
